/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React from 'react';
import { motion } from 'framer-motion';
import { Pill, FileText, Image as ImageIcon, QrCode, ScrollText } from 'lucide-react';
import { cn } from '@/lib/utils';

export const DOC_TYPE_META = {
  referto: { label: 'Referto', gradient: 'from-blue-500 to-indigo-600', icon: FileText },
  esame: { label: 'Esame', gradient: 'from-emerald-500 to-teal-600', icon: FileText },
  ricetta_esterna: { label: 'Ricetta', gradient: 'from-purple-500 to-fuchsia-600', icon: Pill },
  certificato: { label: 'Certificato', gradient: 'from-amber-500 to-orange-600', icon: ScrollText },
  immagine: { label: 'Immagine', gradient: 'from-rose-500 to-pink-600', icon: ImageIcon },
  altro: { label: 'Altro', gradient: 'from-slate-500 to-slate-700', icon: FileText },
};

// ---------- Card (Apple Wallet-style) ----------
export const WalletCard = ({ item, kind, onOpen, onDelete }) => {
  const meta = kind === 'prescription'
    ? { label: 'Prescrizione', gradient: 'from-primary to-blue-600', icon: Pill }
    : DOC_TYPE_META[item.doc_type] || DOC_TYPE_META.altro;
  const Icon = meta.icon;
  const title = kind === 'prescription' ? item.diagnosis || 'Prescrizione medica' : item.title;
  const subtitle = kind === 'prescription' ? `${(item.drugs || []).length} farmaci` : item.description || '';
  const dateStr = new Date(item.issued_at || item.created_at).toLocaleDateString('it', { day: '2-digit', month: 'short', year: 'numeric' });
  const code = item.code;

  return (
    <motion.button
      layout
      whileHover={{ y: -4 }}
      whileTap={{ scale: 0.98 }}
      onClick={() => onOpen(item, kind)}
      className={cn(
        'relative aspect-[16/10] rounded-3xl overflow-hidden text-left text-white group shadow-elev-lg',
        `bg-gradient-to-br ${meta.gradient}`
      )}
      data-testid={`wallet-card-${kind}-${item.id || item.code}`}
    >
      {/* Decorative rings */}
      <div className="absolute -top-16 -right-16 h-40 w-40 rounded-full bg-white/10 blur-2xl pointer-events-none" />
      <div className="absolute -bottom-20 -left-16 h-40 w-40 rounded-full bg-black/20 blur-2xl pointer-events-none" />
      <div className="absolute inset-0 bg-grain opacity-10 pointer-events-none" />

      <div className="relative h-full p-5 flex flex-col">
        <div className="flex items-start justify-between">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/15 backdrop-blur-sm text-[10px] font-bold uppercase tracking-wider">
            <Icon className="h-3 w-3" /> {meta.label}
          </div>
          <div className="text-[10px] font-bold uppercase tracking-wider opacity-80">{dateStr}</div>
        </div>
        <div className="mt-auto">
          <div className="font-display font-bold text-lg leading-tight line-clamp-2">{title}</div>
          {subtitle && <div className="text-xs mt-1 opacity-80 line-clamp-1">{subtitle}</div>}
          <div className="mt-3 flex items-center justify-between">
            <span className="text-[10px] font-mono opacity-70 tracking-widest">{code}</span>
            <div className="h-8 w-8 rounded-lg bg-white/15 backdrop-blur-sm flex items-center justify-center">
              <QrCode className="h-4 w-4" />
            </div>
          </div>
        </div>
      </div>
    </motion.button>
  );
};
